import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuChevronUp } from 'react-icons/lu';

import { createDunningInteraction } from '../../../../services/finance/dunningApi';
import {
   CardHint,
   CardTitle,
   ControlSelect,
   ControlTextarea,
   FieldLabel,
   PrimaryActionButton,
} from '../../../../styles/dunning/base';
import {
   ChannelChip,
   Collapsible,
   CollapseToggle,
   DrawerCardPad,
   InteractionFoot,
   SectionHeadRow,
} from '../../../../styles/dunning/drawer';
import { CHANNELS } from '../constants';
import { canMoveCase } from '../utils/derive';
import { financeErrorMessage } from '../../utils/apiError';
import { formatBRL, formatDateBR } from '../../utils/dunningFormat';
import AttachmentPicker from './AttachmentPicker';

// ---------------------------------------------------------------------------
// Registrar tratativa — append-only (não-negociável nº 6): sem editar, sem
// excluir, e a interface diz isso no subtítulo. Um único POST; o "registrar e
// avançar" vai atômico via `move_to_stage_id`.
//
// ANEXOS (handoff 0096): sobem NO MESMO POST (multipart), porque tratativa e
// evidência nascem juntas e imutáveis — não existe anexar depois (§5.1). Como
// a operação é atômica, o 422 não grava nada: o estado do formulário (texto E
// arquivos) é preservado no erro e só é limpo no sucesso.
//
// SEM `next_action_at` aqui (D14): compromisso é só pelo modal próprio — o
// botão "+ Novo compromisso" fica no cabeçalho do drawer (contexto do caso),
// separado do registro de tratativa.
//
// "Avançar para a raia" nunca oferece TERMINAL_PAID (automática) nem EXTERNAL
// (escalada é só pelo endpoint dedicado, com confirmação).
//
// ⚠️ `canMoveStage` (= finance:dunning:update, handoff 0100): mover raia virou
// gestão, registrar tratativa não. Sem a permissão o select SOME — não fica
// desabilitado nem falha no submit. Motivo: o POST é atômico, então o 403
// `stage_move_requires_permission` descartaria o relato inteiro que o operador
// acabou de digitar. É o operador externo trabalhando o caso sem mover o card,
// por design (0100 §6) — não é limitação a contornar.
//
// ⚠️ Mesma armadilha, causa nova (0103): caso da TRILHA externa não muda de
// raia de jeito nenhum — nem com `:update`. Mandar `move_to_stage_id` ali
// devolve 422 `external_case_not_movable` e, sendo atômico, joga fora o relato
// junto. Por isso o select também some por TRILHA, não só por permissão.
// ---------------------------------------------------------------------------

export default function InteractionForm({
   caseData,
   activeStages,
   canMoveStage,
   onSubmitted,
}) {
   const [channel, setChannel] = useState('PHONE');
   const [content, setContent] = useState('');
   const [chargeId, setChargeId] = useState('');
   const [moveStageId, setMoveStageId] = useState('');
   const [files, setFiles] = useState([]);
   const [isSaving, setIsSaving] = useState(false);
   // Recolhível (2026-08-31). Nasce FECHADO: abrir o caso é, na maioria das
   // vezes, ler o que já aconteceu — o formulário inteiro empurrava o histórico
   // para fora da tela. Quem vai registrar expande. Estado efêmero, não vai
   // para a URL.
   const [isOpen, setIsOpen] = useState(false);
   // % do upload — só existe quando há arquivo (o JSON puro sobe instantâneo).
   const [uploadPercent, setUploadPercent] = useState(null);

   // Trocar de caso com o drawer aberto (link de contraparte da timeline) não
   // desmonta este form — sem isto ele ficaria expandido do caso anterior.
   useEffect(() => {
      setIsOpen(false);
   }, [caseData.id]);

   const openMembers = (caseData.members ?? []).filter(
      (member) => member.member_status === 'OPEN'
   );

   const moveOptions = activeStages.filter(
      (stage) =>
         stage.id !== caseData.stage?.id &&
         stage.stage_type !== 'TERMINAL_PAID' &&
         stage.stage_type !== 'EXTERNAL'
   );

   // Permissão E trilha: ver o ⚠️ do cabeçalho.
   const showMoveStage = canMoveStage && canMoveCase(caseData);

   const handleSubmit = async () => {
      if (!content.trim()) return;
      setIsSaving(true);
      setUploadPercent(files.length > 0 ? 0 : null);
      try {
         const updated = await createDunningInteraction(
            caseData.id,
            {
               channel,
               content: content.trim(),
               chargeId: chargeId ? Number(chargeId) : undefined,
               moveToStageId: moveStageId ? Number(moveStageId) : undefined,
               files,
            },
            {
               onUploadProgress: (event) => {
                  if (!event.total) return;
                  setUploadPercent(
                     Math.round((event.loaded * 100) / event.total)
                  );
               },
            }
         );

         if (moveStageId) {
            const target = moveOptions.find(
               (stage) => String(stage.id) === moveStageId
            );
            toast.success(`Caso movido para ${target?.name ?? 'outra raia'}.`);
         } else {
            toast.success('Tratativa registrada.');
         }

         setChannel('PHONE');
         setContent('');
         setChargeId('');
         setMoveStageId('');
         setFiles([]);
         onSubmitted(updated);
      } catch (err) {
         // Nada foi gravado (operação atômica): o formulário fica intacto —
         // texto e arquivos — para o operador corrigir e reenviar.
         toast.error(
            financeErrorMessage(err, 'Não foi possível registrar a tratativa.')
         );
      } finally {
         setIsSaving(false);
         setUploadPercent(null);
      }
   };

   let submitLabel = 'Registrar';
   if (isSaving) {
      submitLabel =
         uploadPercent !== null && uploadPercent < 100
            ? `Enviando ${uploadPercent}%…`
            : 'Registrando...';
   }

   return (
      <DrawerCardPad>
         <SectionHeadRow>
            <div style={{ flex: 1, minWidth: 0 }}>
               <CardTitle style={{ fontSize: 14, display: 'block' }}>
                  Registrar tratativa
               </CardTitle>
               <CardHint style={{ fontSize: 11 }}>
                  Selecione a origem do contato e descreva a tratativa. Você pode anexar arquivos e vincular a um título.
               </CardHint>
            </div>
            <CollapseToggle
               type="button"
               $open={isOpen}
               aria-expanded={isOpen}
               aria-controls="dunning-interaction-section"
               title={
                  isOpen
                     ? 'Recolher o formulário de tratativa'
                     : 'Expandir o formulário de tratativa'
               }
               onClick={() => setIsOpen((prev) => !prev)}
            >
               <LuChevronUp size={15} />
            </CollapseToggle>
         </SectionHeadRow>

         {/* O corpo inteiro vira UM filho do colapsável e traz o próprio
             espaçamento: assim o `gap` do card conta uma vez só, e recolhido
             não sobra uma faixa vazia embaixo do cabeçalho. */}
         <Collapsible id="dunning-interaction-section" $open={isOpen}>
            <div
               style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
         <div>
            <FieldLabel as="span">Canal *</FieldLabel>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
               {CHANNELS.map((option) => (
                  <ChannelChip
                     key={option.value}
                     type="button"
                     $active={channel === option.value}
                     onClick={() => setChannel(option.value)}
                  >
                     {option.label}
                  </ChannelChip>
               ))}
            </div>
         </div>

         <div>
            <FieldLabel htmlFor="dunning-interaction-content">
               Relato *
            </FieldLabel>
            <ControlTextarea
               id="dunning-interaction-content"
               rows={3}
               maxLength={5000}
               placeholder="O que foi combinado com o cliente"
               value={content}
               onChange={(event) => setContent(event.target.value)}
            />
         </div>

         {/* Ordem do card (decisão 2026-08-19): relato → anexos → vínculo de
             título. A evidência acompanha o que foi relatado; o vínculo é
             classificação, e classificar vem depois de descrever. */}
         <AttachmentPicker
            files={files}
            onChange={setFiles}
            disabled={isSaving}
         />

         <div>
            <FieldLabel htmlFor="dunning-interaction-charge">
               Vincular a um título
            </FieldLabel>
            <ControlSelect
               id="dunning-interaction-charge"
               value={chargeId}
               onChange={(event) => setChargeId(event.target.value)}
            >
               <option value="">Caso inteiro</option>
               {openMembers.map((member) => (
                  <option key={member.charge_id} value={String(member.charge_id)}>
                     {`Parcela ${member.charge?.installment_number ?? '—'} · venc. ${formatDateBR(member.charge?.due_date)} · ${formatBRL(member.charge?.value)}`}
                  </option>
               ))}
            </ControlSelect>
         </div>

         <InteractionFoot>
            {showMoveStage ? (
               <div style={{ flex: 1, minWidth: 180 }}>
                  <FieldLabel htmlFor="dunning-interaction-move">
                     Avançar para a raia
                  </FieldLabel>
                  <ControlSelect
                     id="dunning-interaction-move"
                     value={moveStageId}
                     onChange={(event) => setMoveStageId(event.target.value)}
                  >
                     <option value="">Manter na raia atual</option>
                     {moveOptions.map((stage) => (
                        <option key={stage.id} value={String(stage.id)}>
                           {stage.name}
                        </option>
                     ))}
                  </ControlSelect>
               </div>
            ) : (
               /* Espaçador: sem o select, a ação de escrita continua na ponta
                  direita do rodapé (FC7 G.5). */
               <div style={{ flex: 1 }} />
            )}
            <PrimaryActionButton
               type="button"
               onClick={handleSubmit}
               disabled={!content.trim() || isSaving}
            >
               {submitLabel}
            </PrimaryActionButton>
         </InteractionFoot>
            </div>
         </Collapsible>
      </DrawerCardPad>
   );
}

InteractionForm.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   caseData: PropTypes.object.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   activeStages: PropTypes.array.isRequired,
   canMoveStage: PropTypes.bool.isRequired,
   onSubmitted: PropTypes.func.isRequired,
};
